export default function SystemReconstruction() {
  const reasons = [
    { title: '레거시 시스템 노후화', desc: '유지보수 비용 증가 및 확장성 한계' },
    { title: '성능 저하', desc: '증가하는 트래픽과 데이터 처리 부담' },
    { title: '데이터 활용 제약', desc: '비효율적인 구조로 인한 데이터 활용 어려움' },
  ];

  const approach = [
    {
      icon: 'ri-search-line',
      title: '현황 분석',
      items: ['시스템 아키텍처 분석', '성능 병목 지점 파악', '개선 우선순위 도출'],
    },
    {
      icon: 'ri-draft-line',
      title: '재설계',
      items: ['데이터 기반 구조 설계', '클라우드 전환 검토', '확장성 고려 설계'],
    },
    {
      icon: 'ri-tools-line',
      title: '구현 · 이관',
      items: ['단계적 시스템 전환', '데이터 마이그레이션', '무중단 배포 전략'],
    },
    {
      icon: 'ri-shield-check-line',
      title: '검증 · 안정화',
      items: ['성능 테스트', '보안 점검', '운영 모니터링'],
    },
  ];

  const benefits = [
    { metric: '50%', label: '운영 비용 절감' },
    { metric: '3배', label: '처리 성능 향상' },
    { metric: '99.9%', label: '시스템 안정성' },
  ];

  return (
    <section className="py-36 bg-[#F0FDFA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-20">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#0D9488]/10 border border-[#0D9488]/20 mb-6">
            <span className="text-sm font-medium text-[#0D9488]">Service 02</span>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 leading-tight mb-6">
            System Reconstruction
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl leading-relaxed">
            시스템은 시간이 지날수록<br />
            환경과 맞지 않게 됩니다.
          </p>
          <p className="text-lg text-gray-600 max-w-3xl leading-relaxed mt-4">
            4-lab은 기존 시스템을 분석하여<br />
            데이터 활용과 운영 효율을 고려한<br />
            구조 재구성을 수행합니다.
          </p>
        </div>

        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-10">재구성이 필요한 이유</h3>
          <div className="grid md:grid-cols-3 gap-6">
            {reasons.map((reason, index) => (
              <div key={index} className="bg-white rounded-lg p-8 border border-[#99F6E4]/30">
                <h4 className="text-lg font-bold text-gray-900 mb-3">{reason.title}</h4>
                <p className="text-sm text-gray-600">{reason.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-10">분석 및 접근 방법</h3>
          <div className="grid md:grid-cols-4 gap-6">
            {approach.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-8 border border-[#99F6E4]/30">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14B8A6]/10 mb-6">
                  <i className={`${item.icon} text-2xl text-[#0D9488]`}></i>
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-4">{item.title}</h4>
                <ul className="space-y-2">
                  {item.items.map((subItem, subIndex) => (
                    <li key={subIndex} className="text-sm text-gray-600 flex items-start">
                      <span className="mr-2">·</span>
                      <span>{subItem}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-20">
          <h3 className="text-2xl font-bold text-gray-900 mb-10 text-center">기대 효과</h3>
          <div className="grid md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-[#0D9488] mb-3">{benefit.metric}</div>
                <div className="text-base text-gray-700">{benefit.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-6">프로젝트 사례 요약</h3>
          <div className="bg-white rounded-lg p-10 border border-[#99F6E4]/30">
            <div className="flex items-start mb-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14B8A6]/10 mr-4 flex-shrink-0">
                <i className="ri-building-line text-2xl text-[#0D9488]"></i>
              </div>
              <div>
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  공공기관 레거시 시스템 클라우드 전환
                </h4>
                <p className="text-sm text-gray-600 leading-relaxed">
                  10년 이상 운영된 온프레미스 시스템을 AWS 기반 클라우드로 전환.
                  마이크로서비스 아키텍처 적용으로 시스템 확장성 확보 및 운영 비용 50% 절감.
                  무중단 배포 체계 구축으로 서비스 안정성 향상.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-[#F0FDFA] text-xs font-medium text-[#0F766E] rounded-full">
                AWS
              </span>
              <span className="px-3 py-1 bg-[#F0FDFA] text-xs font-medium text-[#0F766E] rounded-full">
                Kubernetes
              </span>
              <span className="px-3 py-1 bg-[#F0FDFA] text-xs font-medium text-[#0F766E] rounded-full">
                Microservices
              </span>
              <span className="px-3 py-1 bg-[#F0FDFA] text-xs font-medium text-[#0F766E] rounded-full">
                CI/CD
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
